import { useCallback } from 'react';

import { routes } from '~constants/routes';
import { useAgeConfirmationContext } from '~context/age-confirmation';
import { useAuthActionsContext } from '~context/auth';
import type { NsfwReason } from '~features/annals/enums/nsfwReason';
import type {
	RegisterInbound,
	RegisterOutbound,
} from '~features/annals/enums/register';
import annalsNsfw from '~features/annals/hooks/nsfw';
import { useSession } from '~hooks/useSession';
import { usePathname, useRouter } from '~i18n/navigation';
import { useUpdateSafeModeMutation } from '~services/api/auth';

type CallbackOptions = {
	force?: boolean;
	nsfwReason: NsfwReason;
	registerInbound?: RegisterInbound;
	registerOutbound?: RegisterOutbound;
	hideDialog?: boolean;
};

export const useSwitchSafeMode = () => {
	const pathname = usePathname();
	const router = useRouter();
	const { userId } = useSession();

	const [update] = useUpdateSafeModeMutation();

	const annalsSafeModeSwitched = annalsNsfw.useSafeModeSwitched();

	return useCallback(
		(isSafe: boolean, options: Pick<CallbackOptions, 'nsfwReason'>) => {
			const safeModeUpdate = () =>
				update({
					userId,
					isSafeMode: isSafe,
				})
					.unwrap()
					.then((data) => {
						annalsSafeModeSwitched({ resource: options.nsfwReason });
						// Turning on nsfw mode being on index page forwards to /models page
						if (!isSafe && pathname === routes.index) {
							router.push(routes.explore.models);
						}
						return data;
					})
					.catch(() => {
						// noop
					});

			return safeModeUpdate();
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[update, userId, pathname]
	);
};

export const useSwitchSafeModeWithAgeValidation = () => {
	const { openAgeConfirmation } = useAgeConfirmationContext();
	const { openRegister } = useAuthActionsContext();
	const { isDemoUser } = useSession();

	const switchSafeMode = useSwitchSafeMode();

	return useCallback(
		(isSafe: boolean, options: CallbackOptions) => {
			const {
				force,
				nsfwReason,
				registerInbound,
				registerOutbound,
				hideDialog,
			} = options;

			return new Promise((resolve, reject) => {
				const cb = () => {
					if (!isSafe && !hideDialog) {
						openAgeConfirmation({
							nsfwReason,
							resolve,
							reject,
						});
					} else {
						void switchSafeMode(isSafe, { nsfwReason }).then(resolve);
					}
				};
				if (isSafe && !isDemoUser) {
					void switchSafeMode(isSafe, { nsfwReason }).then(resolve);
				} else if (isDemoUser && !force) {
					openRegister({
						cb,
						analytics: {
							inbound: registerInbound,
							outbound: registerOutbound,
						},
					});
				} else {
					cb();
				}
			});
		},
		[openAgeConfirmation, openRegister, isDemoUser, switchSafeMode]
	);
};
